/* eslint-disable react/jsx-curly-newline */
/* eslint-disable react/no-danger */
import React, { useMemo, useEffect, createRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useWindowSize, useWindowScroll } from '@folklore/hooks';
import { useSprings, animated as a } from '@react-spring/web';

import { useModal } from '../../../contexts/ModalsContext';

import * as AppPropTypes from '../../../lib/PropTypes';
import PlusButton from '../../buttons/Plus';
import Button from '../../buttons/Button';
import IntersectingVideo from './IntersectingVideo';
import Html from '../../partials/Html';

import styles from '../../../../styles/blocks/employee/timeline.module.scss';

const propTypes = {
    className: PropTypes.string,
    entries: PropTypes.arrayOf(
        PropTypes.shape({
            year: PropTypes.string,
            color: PropTypes.string,
            details: AppPropTypes.timelineDetails,
        }),
    ),
};

const defaultProps = {
    className: null,
    entries: null,
};

const Timeline = ({ className, entries }) => {
    const { setModal } = useModal();
    const { height: offset } = useWindowSize();
    const { y: scroll = 0 } = useWindowScroll();

    const [springs, setSprings] = useSprings(
        entries?.length,
        () => ({
            transform: 'translateY(-50px)',
            opacity: 0,
        }),
        [],
    );

    // console.log(scroll);

    const refs = useMemo(() => Array.from({ length: springs.length }).map(() => createRef()), []);

    useEffect(() => {
        setSprings((ind) => {
            const { y: posY } = refs[ind].current.getBoundingClientRect();
            const ratio = 1 - posY / offset;

            return {
                transform: ratio > 0.3 ? 'translateY(0)' : 'translateY(-50px)',
                opacity: ratio > 0.3 ? 1 : 0,
            };
        });
    }, [scroll, offset]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            {(springs || []).map((spring, i) => {
                const {
                    color = null,
                    linkColor = null,
                    year = null,
                    details = [],
                } = entries[i] || {};

                return (
                    <a.div
                        key={`timeline-entry-${year}-${i + 1}`}
                        ref={refs[i]}
                        className={styles.timeline}
                        style={{ ...spring, color: `${color}` }}
                    >
                        <div className={styles.arrowContainer}>
                            <div className={styles.year}>{year}</div>
                            <span
                                className={styles.timelineArrow}
                                style={{ backgroundColor: `${color}` }}
                            />
                        </div>
                        {(details || []).map(
                            (
                                {
                                    title = null,
                                    text = null,
                                    image = null,
                                    video = null,
                                    visualLink = null,
                                    verticalImage = false,
                                    modals = [],
                                },
                                detailI,
                            ) => (
                                <div
                                    key={`entry-${title}-${detailI + 1}`}
                                    className={styles.content}
                                >
                                    {title !== null ? (
                                        <Html
                                            className={classNames([styles.title], {
                                                [styles[linkColor]]: linkColor !== null,
                                            })}
                                            body={title}
                                            tag="h3"
                                        />
                                    ) : null}
                                    <div
                                        className={classNames([
                                            styles.inner,
                                            {
                                                [styles.withImage]:
                                                    (image !== null || video !== null) &&
                                                    !verticalImage,
                                            },
                                        ])}
                                    >
                                        {text !== null ? (
                                            <Html
                                                className={classNames([styles.text], {
                                                    [styles[linkColor]]: linkColor !== null,
                                                })}
                                                body={text}
                                            />
                                        ) : null}

                                        {image !== null && visualLink === null ? (
                                            <Button
                                                onClick={() =>
                                                    setModal({
                                                        type: 'timeline',
                                                        image,
                                                        backgroundColor: color,
                                                    })
                                                }
                                                className={classNames([
                                                    styles.visualWrapper,
                                                    {
                                                        [styles.vertical]: verticalImage,
                                                    },
                                                ])}
                                                style={{ border: '0px none' }}
                                            >
                                                <img
                                                    src={image}
                                                    alt=""
                                                    className={classNames([
                                                        styles.timelineImage,
                                                        {
                                                            [styles.vertical]: verticalImage,
                                                        },
                                                    ])}
                                                    style={{ borderColor: `${color}` }}
                                                />
                                            </Button>
                                        ) : null}

                                        {image !== null && visualLink !== null ? (
                                            <a
                                                href={visualLink}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={classNames([
                                                    styles.visualWrapper,
                                                    {
                                                        [styles.vertical]: verticalImage,
                                                    },
                                                ])}
                                                style={{ border: '0px none' }}
                                            >
                                                <img
                                                    src={image}
                                                    alt=""
                                                    className={classNames([
                                                        styles.timelineImage,
                                                        {
                                                            [styles.vertical]: verticalImage,
                                                        },
                                                    ])}
                                                    style={{ borderColor: `${color}` }}
                                                />
                                            </a>
                                        ) : null}

                                        {video !== null && visualLink === null ? (
                                            <IntersectingVideo
                                                src={video}
                                                type="video/mp4"
                                                muted
                                                loop
                                                className={styles.timelineVideo}
                                                style={{ borderColor: `${color}` }}
                                            />
                                        ) : null}

                                        {video !== null && visualLink !== null ? (
                                            <a
                                                href={visualLink}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={styles.visualWrapper}
                                                style={{ border: 'none' }}
                                            >
                                                <IntersectingVideo
                                                    src={video}
                                                    type="video/mp4"
                                                    muted
                                                    loop
                                                    className={styles.videoInner}
                                                    style={{ borderColor: `${color}` }}
                                                />
                                            </a>
                                        ) : null}
                                    </div>

                                    {modals.length > 0 ? (
                                        <div className={styles.modalButtons}>
                                            {modals.map(
                                                (
                                                    {
                                                        title: modalTitle = null,
                                                        text: modalText = null,
                                                        image: modalImage = null,
                                                        video: modalVideo = null,
                                                        layout: modalLayout = 'imageTop',
                                                    },
                                                    modalI,
                                                ) => (
                                                    <PlusButton
                                                        key={`modal-button-${title}-${modalI + i}`}
                                                        color={color}
                                                        pulse
                                                        onClick={() =>
                                                            setModal({
                                                                type: 'timeline',
                                                                title: modalTitle,
                                                                text: modalText,
                                                                image: modalImage,
                                                                video: modalVideo,
                                                                layoutType: modalLayout,
                                                                backgroundColor: color,
                                                            })
                                                        }
                                                        iconClassName={styles.modalIcon}
                                                        className={classNames([
                                                            styles.plusButton,
                                                            {
                                                                [styles.notFirstPlus]: modalI > 0,
                                                            },
                                                        ])}
                                                    />
                                                ),
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            ),
                        )}
                    </a.div>
                );
            })}
        </div>
    );
};

Timeline.propTypes = propTypes;
Timeline.defaultProps = defaultProps;

export default Timeline;
